import { useGenericSelector } from "../Shared/Redux";
import { IApiCallState } from "./ApiCalls/Models";
import { hydrateApiCallState } from "./ApiCalls/Utils";

export const maxPassengerCount = 6;

export const getApiCallState = () => hydrateApiCallState(useStateSelector(state => state.apicalls));
export function useStateSelector<T>(selectorFunc: (state: IAppState) => T) { return useGenericSelector((state :IAppState) => selectorFunc(state)); }

export interface IAppState {
	dialog: IBookingDialogState,
	search: ISearchState;
	timetable: ITimetableState;
	travelDetails: ITravelDetailsState;
	passengersAndPayment: IPassengersAndPaymentState;
	apicalls: IApiCallState;
}

export type BookingDialogStep = "search" | "timetable" | "outbound" | "inbound" | "passengersandpayment" | "seatmap";

export interface IBookingDialogState
{
	step: BookingDialogStep;
	visible: boolean;
}

export interface ISettings
{
	apiUrl: string;
	token: {
		accessToken: string;
		expires: string;
	},
	allowFakePayments: boolean;
	enableSeatmap: boolean;
	enableMealBooking: boolean;
	enableEditMealBooking: boolean;
	mealBookingServiceNames: string[];
	lastTableBookingDate?: string;
	adyenMethods: string[];
	termsUrl?: string;
	receiptUrl?: string;
	paymentFailedUrl?: string;
	manageApp?: {
		hideSearchForm?: boolean;
		bookingNumber?: string;
		email?: string;
		mode?: "" | "PaymentOnly";
	}
}

export interface ISearchState
{
	stops?: IStop[];
	passengerTypes: IPassengerType[];

	defaultFrom?: string;
	defaultTo?: string;

	origin?: IStop;
	destination?: IStop;
	isTwoWay: boolean;
	departureDate?: string;
	returnDate?: string;
	passengers: IPassengerDefinition[];
	travelWithPet: boolean;
	interrail?: boolean;
	interrailValidation?: {
		isValid?: boolean;
		result?: { number: string, valid: boolean }[]
	}
	outboundCalendar?: ICalendar;
	inboundCalendar?: ICalendar
}

export interface ICalendar
{
	origin: string;
	destination: string;
	days: { [key: string]: ICalendarDay };
}

export interface ICalendarDay
{
	amount: number;
	capacity: number;
	quota: number;
}

export interface ITimetableState
{
	searchJourneyResult?: ISearchJourneyResult;
}

export interface ITravelDetailsState
{
	outbound?: ITravelState;
	inbound?: ITravelState;
	tableBookings?: {
		availableTimes?: { [legId: string]: IAvalilableTableTimes[] };
		bookings?: ITemporaryTableBooking[];
	}
}

export interface IAvalilableTableTimes
{
	productCode: string;
	times: string[];
	date: string;
}

export interface ITravelState
{
	route: IRoute;
	bundle?: IBundle;
	options?: {
		[legId: string]: { [tariffCode: string]: number }
	};
};

export interface ITemporaryTableBooking
{
	bookingId?: number;
	time: string;
	date: string;
	persons: number;
	productCode: string;
	legId: string;
	serviceName: string;
	direction: Direction;
	slotName: string;

}


export interface IPassengersAndPaymentState
{
	booking: IBooking;
	paymentMethod?: string;
	paymentData?: IPaymentData;
	vouchers?: IVoucher[];
	discountCodes?: IVoucher[];
	seatChange: {
		tariffSegment?: IBookingTariffSegment;
		carriageLayout?: ICarriageLayouts;
		selectedSeats?: { itemId: string, seat: ISeat }[]
	}
};

export interface ICarriageLayouts
{
	carriages: ICarriageLayout[];
}

export interface ICarriageLayout
{
	carriageName: string;
	carriageNumber: string;
	template?: string;
	seatWidth: number;
	seatHeight: number;
	floorplanDimensions: { width: number; height: number; };
	seats: ICarriageSeat[];
}

export interface ICarriageSeat {
	seatNumber: string;
	xPos: number;
	yPos: number;
	rowNumber: number;
	rowPosition: number;
	available: boolean;
	inventoryClass: string;
	propertyCodes: string[];
}

export interface IVoucher
{
	voucherCode: string;
	amount: number;
	percent?: number;
}

export interface IPaymentData
{
	method: "adyen"|"swish"|"fake"|"none";
	amount: number;
	adyenData?: {
		sessionId: string;
		checkoutKey: string;
		postData: { sessionData: string; environment: "test"|"live" }
	}
}

export interface IStop
{
	code: string;
	shortCode?: string;
	name: string;
	synonyms: string[];
}

export interface IPassengerType
{
	code: string;
	description: string;
	ageFrom?: number;
	ageTo?: number;
}

export interface IPassengerDefinition {
	type?: IPassengerType;
}



export interface ISearchJourneyResult
{
	offer?: IOffer;
}

export interface IOffer
{
	productFamilies: IProductFamily[];
	comfortZones: IDataItem[];
	passengers: { id: string, type: string }[];
	travels: ITravel[];
}

export interface IProductFamily extends IDataItem
{
	travelClass?: string;
	comfortClass?: string;
	flexLevel?: string;
}

export interface IDataItem
{
	id?: string;
	code: string;
	name: string;
}

export type Direction = "inbound"|"outbound"
export interface ITravel
{
	id: string;
	destinationStation: IJourneySearchStation;
	originStation: IJourneySearchStation;
	direction: Direction;
	departureDate: string;
	routes: IRoute[];
}

export interface IRoute
{
	id: string;
	bundleAvailability: {
		availability: boolean;
	}
	travelDuration: string;
	legs: IRouteLeg[];
	bundles: IBundle[];
	lowestPrice?: number;
}

export interface IRouteLeg
{
	id: string;
	departureStation: IJourneySearchStation;
	arrivalStation: IJourneySearchStation;
	serviceType: IServiceType;
	serviceIdentifier: string;
	serviceName: string;
	serviceInformation?: IServiceInformation;
	salesRestrictions?: { reasonCode: string, salesEndDate?: string }[];
}

export interface IServiceType
{
	code: string;
	name: string;
	modality?: string;
}

export interface ISeat
{
	carriage: string;
	number: string;
	compartment?: string
}

export interface IJourneySearchStation
{
	name: string;
	uicStationCode: string;
	arrivalTimestamp?: string;
	departureTimestamp?: string;
}

export interface IServiceInformation
{
	name: string;
	serviceProperties: { code: string; description: string; }[];
	travelInfo: { title: string; content: string }[];
}

export interface IBundle
{
	id: string;
	price: number;
	productFamilyId: string;
	requiredItems: IRequiredItem[];
	options: IBundleItem[];
	flexibility: Flexibility;
};

export type Flexibility = "nonrefundable" | "refundable" | "rebookable";

export interface IRequiredItem extends IBundleItem
{
	price: number;
}


export interface IBundleItem
{
	id: string;
	comfortZoneCode: string;
	description: string;
	legIds: string[];
	name: string;
	productCode: string;
	passengerFares: { passengerId: string; tariffCode: string; price: number }[];
}

export interface IBooking
{
	bookingNumber: string;
	payments: any[];
	bookingFulfillmentMethod?: {
		code: string;
		name: string;
		description: string;
	},
	customer?: IBookingCustomer;
	passengers: IBookingPassenger[];

	outbound?: IBookingJourney;
	inbound?: IBookingJourney;

	totalPrice: number;
	totalVat: number;
	totalPricePaid: number;
	totalPriceToBePaid: number;
	totalVatToBePaid: number;

	createTimestamp: string;
	expiryTimestamp: string;
	notes?: any;
	language: string;
	isProvisional?: boolean;
	isFinalized?: boolean;
	isPaid?: boolean;
	requireBirthDateAndCountry: boolean;
}

export interface IBookingJourney
{
	bookingTariffSegments: IBookingTariffSegment[];
	departureStation: IJourneySearchStation;
	arrivalStation: IJourneySearchStation;
	departure: string;
	arrival: string;
	totalSegmentCount: number;
}

export interface IBookingCustomer
{
	firstName?: string;
	lastName?: string;
	email?: string;
	phoneNumber?: string;
}

export interface IBookingPassenger
{
	id: string;
	type: string;
	firstName?: string;
	lastName?: string;
	dateofBirth?: string;
	age?: number;
	countryOfResidence?: string;
	email?: string;
	phoneNumber?: string;
}

export interface IBookingTariffSegment
{
	id: string;
	departureStation: IJourneySearchStation;
	arrivalStation: IJourneySearchStation;
	departure: string;
	arrival: string;
	travelDate: string;
	validityService: string;
	comfortZoneCode?: string;
	tariffGroupCode: string;
	bookingJourneySegments?: IBookingJourneySegment[];
	requiredProducts: IProduct[];
	additionalProducts?: IProduct[];
	serviceType: IServiceType;
	serviceIdentifier: string;
}

export interface IBookingJourneySegment
{
	departureStation: IJourneySearchStation;
	arrivalStation: IJourneySearchStation;
	serviceName: string;
	serviceType: IServiceType;
	departureDateTime: string;
	arrivalDateTime: string;
}

export interface IProduct
{
	code: string;
	name: string;
	description: string;
	tariffCode: string;
	inventoryClass: string;
	passengerId: string;
	itemId: string;
	price: number;
	type: string;
	ticketNumber: string;
	seat?: ISeat;
	canChangeSeat: unknown;
}

export interface IPaymentFields
{
	paymentMethod?: string;
}

export interface IPaymentSessionData
{
	id: string;
	provider: string;
	created: string;
	result?: "AUTHORISED"|"FAILURE";
	pspReference?: string;
}

export interface IBookingSummary {
	bookingNumber: string;
	created: string;
	totalPrice: number;
	amountPaid: number;
	outbound?: IBookingTravelSummary;
	inbound?: IBookingTravelSummary;
}

export interface IBookingTravelSummary {
	from: IStation;
	to: IStation;
	departure: string;
	arrival: string;
	segments?: ITravelSegment[];
	hasDeparted: boolean;
	hasArrived: boolean;
	cancelled: boolean;
	canBeCancelled: boolean;
	// canBeRefunded: boolean;
	refundVouchersOnly: boolean;
	refundAmount: number;
}

export interface ITravelSegment {
	segmentId: string;
	from: IStation;
	to: IStation;
	departure: string;
	arrival: string;
	duration?: string;
	serviceName: string;
	serviceType: IServiceType;
	seats?: ISeat[];
	options?: IOption[];
}

export interface IOption
{
	code: string;
	tariffCode: string;
	name: string;
	description?: string;
	count: number;
	provisionalCount: number;
	price: number;
}

export interface IStation {
	uICStationCode: string;
	name: string;
	shortCode?: string;
	countryCode?: string;
}